import { useCategories } from "@/api/cms";
import { useSaneModifiableList } from "@/api/modifiableList";
import { constructLocaleDict, getMainLanguage, SupportedLocale } from "@/i18n";
import {
  CatalogueRaisonneSection,
  CatalogueRaisonneSectionUpsert,
} from "@/lib/cms/cms-types";
import { generateImageName } from "@/lib/utils";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as AF from "../form-items/AbstractForm";
import { ModifiableList } from "../form-items/ModifiableList";
import { MultilingualField } from "../form-items/MultilingualField";
import AddCroppedImageForm from "../images/AddCroppedImageForm";
import StyledDrawer from "../StyledDrawer";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";

const TypeOeuvreTextField = ({
  name,
  label,
  lang,
  categoryId,
  form,
  tooltipText,
}: {
  name: string;
  label: string;
  lang: SupportedLocale;
  categoryId: string | undefined;
  form: UseFormReturn<any>;
  tooltipText?: string;
}) => {
  const mainLang = getMainLanguage();
  const { data: mainLangData } = useSaneModifiableList(
    "liste-types-oeuvres",
    mainLang,
  );

  // Find the order of the entry matching categoryId in main language
  const matchingOrder = useMemo(() => {
    if (!categoryId || !mainLangData) return null;
    const match = mainLangData.find((item) => item.value === categoryId);
    return match?.order ?? null;
  }, [categoryId, mainLangData]);

  // Find the corresponding value in the current language
  const prefillValue = useMemo(() => {
    if (matchingOrder === null) return "";
    return (
      mainLangData?.find((item) => item.order === matchingOrder)?.value ?? ""
    );
  }, [matchingOrder, mainLangData]);

  // Update the field value when prefillValue changes based on categoryId
  useEffect(() => {
    if (prefillValue && categoryId) {
      form.setValue(name, prefillValue, { shouldDirty: false });
    }
  }, [prefillValue, categoryId, name, form]);

  return (
    <ModifiableList
      listKey="liste-types-oeuvres"
      name={name}
      label={label}
      placeholder={label}
      lang={lang}
      tooltipText={tooltipText}
    />
  );
};

const DecoupageCatalogueRaisonneForm = ({
  form,
  target,
  sections,
  cmsID,
}: {
  form: UseFormReturn<{
    decoupages?: CatalogueRaisonneSectionUpsert[];
    type?: string | undefined;
    target?: "ARTWORKS" | "MULTIPRINTS" | undefined;
  }>;
  target: "ARTWORKS" | "MULTIPRINTS";
  sections: CatalogueRaisonneSection[];
  cmsID: string;
}) => {
  const { t } = useTranslation();

  const { control, watch } = form;

  const selectableCategoryTypes = ["categories", "series", "themes", "type"];

  const selectedType = watch("type");
  const showCategoryFields = selectableCategoryTypes.includes(
    selectedType as string,
  );
  const categoryLabel = t("categoryLabel", {
    defaultValue: t("categoryLabel"),
  });

  const { data: categoriesData, isLoading: isLoadingCategories } =
    useCategories(selectedType);

  const { fields, append, remove, update, replace } = useFieldArray({
    control,
    name: "decoupages",
  });

  useEffect(() => {
    if (!selectedType) return;

    const filtered = sections
      .filter((s) => s.type === selectedType)
      .map((section) => ({
        ...section,
        categoryMultilingual:
          section.categoryMultilingual ?? constructLocaleDict(() => ""),
      }));

    // replace re-initialises the fieldArray while keeping RHF state happy
    replace(filtered);
  }, [selectedType, sections, replace]);

  return (
    <AF.Col>
      <AF.Row className="mb-6">
        <h2 className="text-xl font-bold mb-4 w-full text-left">
          {t("decoupageType")}
        </h2>
        <AF.SelectField
          name="type"
          className="w-full"
          label={""}
          options={[
            { value: "creationDate", label: t("creationDate") },
            { value: "categories", label: t("oeuvresDescriptionCategorie") },
            { value: "series", label: t("oeuvresDescriptionSerie") },
            { value: "themes", label: t("oeuvresDescriptionTheme") },
            { value: "registrationNumber", label: t("registrationNumber") },
            { value: "type", label: t("typeOeuvre") },
          ]}
          placeholder={t("selectType")}
        />
      </AF.Row>
      <Separator className="my-4" />
      <div className="grid grid-cols-2 gap-9 w-full">
        {fields.map((field, index) => (
          <div key={field.id} className="mb-8 rounded-lg border p-4">
            <AF.Row className="mb-4">
              <h3 className="text-lg font-semibold">
                {t("decoupage")} {index + 1}
              </h3>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="bg-transparent hover:bg-transparent"
                onClick={() => remove(index)}
              >
                <Trash2 className="h-5 w-5 text-red-500 hover:text-red-900 transition-colors" />
              </Button>
            </AF.Row>

            {showCategoryFields ? (
              <AF.SelectField
                label={""}
                name={`decoupages.${index}.categoryId`}
                options={
                  categoriesData?.map((category: any) => ({
                    value: category.value,
                    label: category.value,
                  })) ?? []
                }
                placeholder={`${t("select" + selectedType?.charAt(0).toUpperCase() + selectedType?.slice(1))}`}
                disabled={isLoadingCategories}
                className="mb-4"
              />
            ) : (
              <AF.Row className="mb-4">
                <AF.Text
                  name={`decoupages.${index}.startValue`}
                  label={
                    selectedType === "creationDate"
                      ? t("startYear")
                      : t("startNumber")
                  }
                  placeholder={
                    selectedType === "creationDate"
                      ? t("startYear")
                      : t("startNumber")
                  }
                />
                <AF.Text
                  name={`decoupages.${index}.endValue`}
                  label={
                    selectedType === "creationDate"
                      ? t("endYear")
                      : t("endNumber")
                  }
                  placeholder={
                    selectedType === "creationDate"
                      ? t("endYear")
                      : t("endNumber")
                  }
                />
              </AF.Row>
            )}
            <AF.Checkbox
              name={`decoupages.${index}.showContent`}
              field={{
                defaultValue: watch(`decoupages.${index}.showContent`) ?? false,
              }}
              label={t("catalogueRaisonne.decoupage.showContent")}
              className="mb-4"
            />
            <MultilingualField
              fixed={getMainLanguage()}
              render={(lang) => {
                const categoryId =
                  watch(`decoupages.${index}.categoryId`) ?? undefined;
                const showContent =
                  watch(`decoupages.${index}.showContent`) ?? false;
                return (
                  <div className="flex flex-col gap-2">
                    {selectedType === "type" && (
                      <TypeOeuvreTextField
                        name={`decoupages.${index}.categoryMultilingual.${lang}`}
                        label={categoryLabel}
                        lang={lang}
                        categoryId={categoryId}
                        form={form}
                        tooltipText={t("typeOeuvreTooltip")}
                      />
                    )}
                    <AF.Text
                      name={`decoupages.${index}.nameMultilingual.${lang}`}
                      label={t("sectionName")}
                      placeholder={t("sectionName")}
                    />
                    {showContent && (
                      <div className="flex flex-col gap-2">
                        <AF.Text
                          name={`decoupages.${index}.titleMultilingual.${lang}`}
                          label={t("titleSection")}
                          placeholder={t("titleSection")}
                        />
                        <AF.RichText
                          name={`decoupages.${index}.contentMultilingual.${lang}`}
                          label={t(
                            "catalogueRaisonne.decoupage.presentationText",
                          )}
                        />
                      </div>
                    )}
                  </div>
                );
              }}
            />
            <MultilingualField
              className="mt-4"
              excludeLang={getMainLanguage()}
              render={(lang) => {
                const showContent =
                  watch(`decoupages.${index}.showContent`) ?? false;
                return (
                  <div className="flex flex-col gap-2">
                    {selectedType === "type" && (
                      <ModifiableList
                        listKey="liste-types-oeuvres"
                        name={`decoupages.${index}.categoryMultilingual.${lang}`}
                        label={categoryLabel}
                        placeholder={categoryLabel}
                        lang={lang}
                        tooltipText={t("typeOeuvreTooltip")}
                      />
                    )}
                    <AF.Text
                      name={`decoupages.${index}.nameMultilingual.${lang}`}
                      label={t("sectionName")}
                      placeholder={t("sectionName")}
                    />
                    {showContent && (
                      <div className="flex flex-col gap-2">
                        <AF.Text
                          name={`decoupages.${index}.titleMultilingual.${lang}`}
                          label={t("titleSection")}
                          placeholder={t("titleSection")}
                        />
                        <AF.RichText
                          name={`decoupages.${index}.contentMultilingual.${lang}`}
                          label={t(
                            "catalogueRaisonne.decoupage.presentationText",
                          )}
                        />
                      </div>
                    )}
                  </div>
                );
              }}
            />

            <Separator className="my-6" />

            <AF.Col className="mt-4">
              <AF.Row className="mb-1">
                <p className="block text-sm font-medium mb-1">
                  {t("vignetteImage")}
                </p>
                <div className="flex-row items-center">
                  <StyledDrawer
                    className="min-w-full"
                    title={t("addImage")}
                    trigger={
                      <Button type="button" variant="outline" size="sm">
                        {field.vignette ? t("changePhoto") : t("addPhoto")}
                      </Button>
                    }
                  >
                    <AddCroppedImageForm
                      attachment={field.vignette ?? undefined}
                      refetch={() => {}}
                      imagePosition={`decoupage${target}-${index}`}
                      docId={cmsID}
                      aspect={1.5}
                      buildImageName={(instanceName) =>
                        generateImageName({
                          primaryType: "thumbnail",
                          instanceName,
                          pageNameInDefaultLanguage: "CatalogueRaisonne",
                          title: `decoupage-${index}`,
                        })
                      }
                      onSuccess={(attachment) => {
                        if (!attachment.id) return;

                        const currentValues = form.getValues(
                          `decoupages.${index}`,
                        );

                        update(index, {
                          ...currentValues,
                          vignette: {
                            ...attachment,
                            id: attachment.id,
                          },
                        });
                      }}
                      forceCreateNewAttachment={true}
                    />
                  </StyledDrawer>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mx-1"
                    onClick={() => {
                      update(index, {
                        ...fields[index],
                        vignette: null,
                      });
                    }}
                  >
                    {t("removePhoto")}
                  </Button>
                </div>
              </AF.Row>
              {field.vignette && (
                <img
                  src={field.vignette.mediumPublicUrl}
                  alt="Vignette"
                  className="w-full h-auto"
                />
              )}
            </AF.Col>
          </div>
        ))}
      </div>
      <Separator className="my-4" />
      <Button
        type="button"
        variant="ghost"
        className="w-full bg-transparent hover:text-white"
        onClick={() =>
          append({
            nameMultilingual: {
              fr: "",
              en: "",
            },
            titleMultilingual: {
              fr: "",
              en: "",
            },
            contentMultilingual: {
              fr: "",
              en: "",
            },
            showContent: false,
            categoryMultilingual: constructLocaleDict(() => ""),
            startValue: "",
            endValue: "",
            categoryId: "",
            order: fields.length + 1,
            vignette: null,
            type: selectedType ?? "creationDate",
          })
        }
      >
        {t("addDecoupage")}
      </Button>
    </AF.Col>
  );
};

export default DecoupageCatalogueRaisonneForm;
